// This code has been partially generated by an AI.
import { patchState, signalStore, type, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { computed, effect, inject } from '@angular/core';
import { addEntities, addEntity, entityConfig, EntityId, removeEntities, setAllEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap, iif, map, of, pipe, switchMap, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { effectOnceIf } from 'ngxtension/effect-once-if';
import { SkillCoreApi } from '@app/core/services/api/skill-core.api';
import { SkillDto } from '@app/core/model/skill-dto';
import { UUID } from '@app/_api/identifyable';
import { setInitialized, withAsyncInitialization } from '@app/pages/home/tree/store/feature/async-initialization.feature';
import { SkillsStore } from '@app/pages/home/tree/store/skills.store';
import { SkillAssignmentsStore } from '@app/pages/home/tree/store/skill-assignments.store';
import { TreeFilterApi, TreeFilterState } from '@app/pages/home/api/tree-filter.api';
import { TreeFilterUtil } from '@app/pages/home/tree/tree-canvas/utils/tree-filter.util';
import { TreeViewDataService } from '@app/core/services/tree-view.data.service';
import { VisibleSkillMapperUtil } from '@app/shared/skill/visible-skill-mapper.util';

const visibleSkillIdsConfig = entityConfig({
  collection: 'visibleSkillIds',
  entity: type<UUID>(),
  selectId: id => id,
});

const onlyAssignmentsVisibleSkillIdsConfig = entityConfig({
  collection: 'onlyAssignmentsVisibleSkillIds',
  entity: type<UUID>(),
  selectId: id => id,
});

const otherEmployeeVisibleSkillIdsConfig = entityConfig({
  collection: 'otherEmployeeVisibleSkillIds',
  entity: type<UUID>(),
  selectId: id => id,
});

export const TreeSkillsStore = signalStore(
  { providedIn: 'root' },
  withAsyncInitialization(),
  withState<TreeFilterState & { focusedSkillId: UUID | undefined }>({
    onlyAssignments: false,
    tags: [],
    focusedSkillId: undefined,
  }),
  withEntities(visibleSkillIdsConfig),
  withEntities(onlyAssignmentsVisibleSkillIdsConfig),
  withEntities(otherEmployeeVisibleSkillIdsConfig),
  withMethods(
    (
      store,
      skillsStore = inject(SkillsStore),
      skillAssignmentsStore = inject(SkillAssignmentsStore),
      treeViewDataService = inject(TreeViewDataService),
      treeFilterApi = inject(TreeFilterApi),
    ) => ({
      getRootId() {
        return skillsStore.$root()?.id || '';
      },
      getAllSkillIds() {
        return skillsStore.entities().map(skill => skill.id);
      },
      getRootWithAssignedSkillIds() {
        return [this.getRootId(), ...skillAssignmentsStore.entities().map(assignment => assignment.skillId)];
      },
      getFirstLevelSkillIds() {
        return skillsStore.$firstLevelSkills().map(skill => skill.id);
      },
      getFirstLevelAssignedSkillIds() {
        return skillsStore
          .$firstLevelSkills()
          .filter(skill =>
            skillAssignmentsStore
              .entities()
              .map(as => as.skillId)
              .includes(skill.id),
          )
          .map(skill => skill.id);
      },
      getFirstLevelTaggedSkills() {
        return this.getFirstLevelSkillIds().filter(skillId => store.tags().includes(skillId));
      },
      getDescendantIds(id: string): string[] {
        const children = skillsStore.findChildrenIds(id);
        return [...children, ...children.flatMap(childId => this.getDescendantIds(childId))];
      },
      getChildrenIds(parentId: UUID) {
        return skillsStore
          .entities()
          .filter(skill => skill.parentId === parentId)
          .map(skill => skill.id);
      },
      getAllExceptHiddenSkillIds(hiddenSkillIds: Set<string>) {
        return this.getAllSkillIds().filter(skillId => !hiddenSkillIds.has(skillId));
      },
      getAllSkillIdsForTaggedFirstLevelSkills() {
        const hiddenSkillIds = new Set(
          this.getFirstLevelSkillIds()
            .filter(skillId => !store.tags().includes(skillId))
            .flatMap(skillId => [skillId, ...this.getDescendantIds(skillId)]),
        );
        return this.getAllExceptHiddenSkillIds(hiddenSkillIds);
      },
      getAllSkillIdsForAssignedFirstLevelSkills() {
        const assignedFirstLevelSkillIds = new Set(this.getFirstLevelAssignedSkillIds());
        const hiddenSkillIds = new Set(
          this.getFirstLevelSkillIds()
            .filter(skillId => !assignedFirstLevelSkillIds.has(skillId))
            .flatMap(skillId => [skillId, ...this.getDescendantIds(skillId)]),
        );
        return this.getAllExceptHiddenSkillIds(hiddenSkillIds);
      },
      getConfig() {
        if (store.onlyAssignments()) {
          return onlyAssignmentsVisibleSkillIdsConfig;
        }
        if (treeViewDataService.$employeeView()) {
          return otherEmployeeVisibleSkillIdsConfig;
        }
        return visibleSkillIdsConfig;
      },
      updateFilter(): void {
        if (treeViewDataService.$employeeView()) return;
        treeFilterApi
          .updateTreeFilterState({
            onlyAssignments: store.onlyAssignments(),
            tags: store.tags(),
          })
          .subscribe();
      },
      removeVisibleIds(ids: string[]) {
        patchState(store, removeEntities(ids, this.getConfig()));
      },
      addVisibleIds(ids: string[]) {
        patchState(store, addEntities(ids, this.getConfig()));
      },
      setVisibleIds(ids: string[]) {
        patchState(store, setAllEntities(ids, this.getConfig()));
      },
    }),
  ),
  withComputed((store, treeViewDataService = inject(TreeViewDataService)) => ({
    visibleSkillIds: computed((): EntityId[] => {
      if (store.onlyAssignments()) {
        return store.onlyAssignmentsVisibleSkillIdsEntities();
      }
      if (treeViewDataService.$employeeView()) {
        return store.otherEmployeeVisibleSkillIdsEntities();
      }
      return store.visibleSkillIdsEntities();
    }),
  })),
  withComputed((store, skillsStore = inject(SkillsStore), skillAssignmentsStore = inject(SkillAssignmentsStore)) => ({
    visibleSkills: computed(() => {
      const skillIds = store.visibleSkillIds();
      const skills = skillIds.map(id => skillsStore.entityMap()[id]).filter((s): s is SkillDto => !!s);

      const skillAssignments = skillAssignmentsStore.entityMap();
      const firstLevelSkills = skillsStore.$firstLevelSkills();
      const allSkills = skillsStore.entities();

      const UISkills = VisibleSkillMapperUtil.skillsWithExperienceLevelAndColor(skills, skillAssignments, firstLevelSkills, allSkills);

      return UISkills.sort((a, b) => a.title.localeCompare(b.title));
    }),
  })),
  withMethods(
    (
      store,
      skillCoreApi = inject(SkillCoreApi),
      treeFilterApi = inject(TreeFilterApi),
      treeViewDataService = inject(TreeViewDataService),
    ) => ({
      toggleOnlyAssignments() {
        this.setOnlyAssignments(!store.onlyAssignments());
      },
      setOnlyAssignments(onlyAssignments: boolean) {
        patchState(store, { onlyAssignments });
        store.updateFilter();
      },
      removeDescendantSkills(parentId: string) {
        store.removeVisibleIds(store.getDescendantIds(parentId));
      },

      removeSkillWithDescendantSkills(parentId: string) {
        store.removeVisibleIds([parentId, ...store.getDescendantIds(parentId)]);
      },

      showChildren(parentId: UUID) {
        store.addVisibleIds(store.getChildrenIds(parentId));
      },

      showAllSkills() {
        const skillIds =
          store.onlyAssignments() || treeViewDataService.$employeeView()
            ? store.getAllSkillIdsForAssignedFirstLevelSkills()
            : store.getAllSkillIdsForTaggedFirstLevelSkills();
        store.setVisibleIds(skillIds);
      },

      showFirstLevelSkills() {
        if (store.onlyAssignments() || treeViewDataService.$employeeView()) {
          this.showFirstLevelAssignedSkills();
        } else {
          this.showFirstLevelTaggedSkills();
        }
      },

      showFirstLevelTaggedSkills() {
        const skillIds = [store.getRootId(), ...store.getFirstLevelTaggedSkills()];
        store.setVisibleIds(skillIds);
      },

      showFirstLevelAssignedSkills() {
        const skillIds = [store.getRootId(), ...store.getFirstLevelAssignedSkillIds()];
        store.setVisibleIds(skillIds);
      },

      initVisibleSkillIds() {
        patchState(store, setAllEntities([store.getRootId(), ...store.getFirstLevelTaggedSkills()], visibleSkillIdsConfig));
        patchState(store, setAllEntities([store.getRootId(), ...store.getFirstLevelSkillIds()], otherEmployeeVisibleSkillIdsConfig));
      },

      addSkillFamily(skillId: string) {
        return iif(
          () => store.visibleSkillIds().includes(skillId),
          of<void>(undefined),
          skillCoreApi.fetchSkillFamilyIds(skillId).pipe(
            map(familyIds => {
              store.addVisibleIds(familyIds);
            }),
          ),
        );
      },

      addTag(id: UUID): void {
        if (store.tags().includes(id)) return;
        patchState(store, { tags: [...store.tags(), id] });
        patchState(store, addEntity(id, visibleSkillIdsConfig));
        store.updateFilter();
      },

      addTags(ids: UUID[]): void {
        const newIds = [...new Set(ids.filter(id => !store.tags().includes(id)))];
        patchState(store, { tags: [...store.tags(), ...newIds] });
        patchState(store, addEntities(newIds, visibleSkillIdsConfig));
        store.updateFilter();
      },

      removeTag(id: UUID): void {
        if (!store.tags().includes(id)) return;
        patchState(store, { tags: store.tags().filter(tag => tag !== id) });
        this.removeSkillWithDescendantSkills(id);
        store.updateFilter();
      },

      reloadInitialSkills() {
        treeFilterApi.getTreeFilterState().subscribe({
          next: state => {
            patchState(
              store,
              {
                tags: state.tags,
                onlyAssignments: state.onlyAssignments,
              },
              setInitialized(),
            );
            this.initVisibleSkillIds();
          },
        });
      },
    }),
  ),
  withMethods(
    (
      store,
      skillsStore = inject(SkillsStore),
      skillAssignmentsStore = inject(SkillAssignmentsStore),
      treeFilterApi = inject(TreeFilterApi),
    ) => ({
      connectSearch: rxMethod<string>(
        pipe(
          filter(searchTerm => searchTerm.trim().length > 0),
          map(searchTerm => TreeFilterUtil.searchSkill(searchTerm, skillsStore.entities())),
          filter((skillResult): skillResult is SkillDto => !!skillResult),
          filter(({ id }) => !store.onlyAssignments() || !!skillAssignmentsStore.entityMap()[id]),
          switchMap(skillResult => store.addSkillFamily(skillResult.id).pipe(map(() => skillResult))),
          tap(skillResult => store.addTag(TreeFilterUtil.findBranch(skillResult, skillsStore.entityMap()))),
          tap(skillResult => patchState(store, { focusedSkillId: skillResult.id })),
        ),
      ),
      reloadFilterState: rxMethod<void>(
        pipe(
          exhaustMap(() => treeFilterApi.getTreeFilterState()),
          tap(state => {
            patchState(
              store,
              {
                tags: state.tags,
                onlyAssignments: state.onlyAssignments,
              },
              setInitialized(),
            );
          }),
        ),
      ),
    }),
  ),
  withHooks({
    onInit(
      store,
      skillAssignmentsStore = inject(SkillAssignmentsStore),
      skillsStore = inject(SkillsStore),
      treeViewDataService = inject(TreeViewDataService),
    ) {
      effectOnceIf(
        () => skillAssignmentsStore.isInitialized() && skillsStore.isInitialized(),
        () => {
          store.reloadInitialSkills();
        },
      );
      effect(() => {
        if (treeViewDataService.$employeeView()) {
          store.setOnlyAssignments(true);
        }
      });
      effect(() => {
        if (store.onlyAssignments()) {
          store.setVisibleIds(store.getRootWithAssignedSkillIds());
        }
      });
    },
  }),
);
